import { useEffect, useEffectEvent, type ReactNode } from "react";
import { Drawer, Grid, type DrawerProps } from "../ui";
import { windowHeight } from "../theme/displayZoom";
import { drawerWidths } from "./drawerWidths";
import { useDrawerCloseGuard } from "./hooks/useDrawerCloseGuard";
import { DrawerDraftProvider, useDrawerDraft, useDrawerDraftScope } from "./hooks/useDrawerDraftScope";

type DraftLoss = boolean | (() => boolean);
type FullscreenDrawerProps = Omit<DrawerProps, "width" | "height" | "size" | "placement" | "onClose"> & {
  onClose: () => void;
  dirty: DraftLoss;
  busy?: boolean;
  /** Responsive Arbeitsfläche oder stets von unten geöffneter Vollbild-Assistent. */
  presentation?: "responsive" | "bottom";
};

/** Für eine vollständige eingebettete Arbeitsfläche bzw. einen mehrstufigen Vollbildablauf. */
export function FullscreenDrawer({ dirty, busy = false, presentation = "responsive", onClose, ...props }: FullscreenDrawerProps) {
  const mobile = Grid.useBreakpoint().md === false;
  const bottom = presentation === "bottom" || mobile;
  const scope = useDrawerDraftScope();
  const hasChanges = () => (typeof dirty === "function" ? dirty() : dirty) || scope.hasChanges();
  useDrawerDraft(hasChanges, props.open, () => busy || scope.isBusy());
  const requestClose = useDrawerCloseGuard(() => { if (!scope.isBusy()) onClose(); }, hasChanges, !busy, props.open);
  const close = () => { if (!scope.isBusy()) requestClose(); };
  return <DrawerDraftProvider value={scope}><Drawer {...props} onClose={close} placement={bottom ? "bottom" : "right"}
    width={bottom ? undefined : "100%"} height={bottom ? windowHeight("100dvh", presentation === "bottom" ? undefined : "8px") : undefined}
    closable={!busy && (props.closable ?? true)} keyboard={!busy && (props.keyboard ?? true)} maskClosable={!busy && (props.maskClosable ?? true)} /></DrawerDraftProvider>;
}

type LiveInspectorDrawerProps = {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  dirty: DraftLoss;
  busy?: boolean;
  width?: typeof drawerWidths.standard | typeof drawerWidths.wide;
  headerActions?: ReactNode;
  className?: string;
  rootClassName?: string;
  children: ReactNode;
};

/** Modeloser Inspektor: Änderungen wirken im Elternentwurf, die Vorschau bleibt bedienbar. */
export function LiveInspectorDrawer({ open, title, onClose, dirty, busy = false, width = drawerWidths.standard,
  headerActions, className, rootClassName, children }: LiveInspectorDrawerProps) {
  const mobile = Grid.useBreakpoint().md === false;
  const close = useDrawerCloseGuard(onClose, dirty, !busy, open);
  useDrawerDraft(dirty, open, busy);
  const outsideClose = useEffectEvent(close);
  useEffect(() => {
    if (!open) return;
    const pointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Untergeordnete Drawer, Bestätigungen und alle Kit-Portale gehören zur laufenden Bedienung.
      if (target.closest(".ck-sheet, .ck-modal-root, .ck-overlay-positioner, .ck-message, .ck-notification")) return;
      outsideClose();
    };
    document.addEventListener("pointerdown", pointerDown);
    return () => document.removeEventListener("pointerdown", pointerDown);
  }, [open]);
  return <Drawer open={open} title={title} onClose={close} mask={false} closable={!busy} keyboard={!busy}
    placement={mobile ? "bottom" : "right"} width={mobile ? undefined : width} height={mobile ? windowHeight("88dvh") : undefined}
    className={className} rootClassName={rootClassName} extra={headerActions}>{children}</Drawer>;
}

type NavigationDrawerProps = Pick<DrawerProps, "open" | "className" | "styles" | "aria-label" | "children"> & { onClose: () => void };

/** Ausschließlich die mobile Bereichsnavigation des gemeinsamen AppShell. */
export function NavigationDrawer(props: NavigationDrawerProps) {
  return <Drawer {...props} placement="bottom" closable={false} size={windowHeight("85vh")} />;
}
